import { saveAs } from "file-saver";

export const readBlobAsDataUrl = (blob: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      if (typeof reader.result !== "string") {
        reject();
        return;
      }
      resolve(reader.result);
    };

    reader.readAsDataURL(blob);
  });

export const tryFetchAsDataUrl = async (url: string): Promise<string | null> => {
  try {
    const response = await fetch(url);
    if (!response.ok) return null;

    const blob = await response.blob();
    return await readBlobAsDataUrl(blob);
  } catch {
    return null;
  }
};

export const runWithConcurrency = async <T>(
  items: T[],
  concurrency: number,
  task: (item: T) => Promise<void>
) => {
  if (items.length === 0) return;

  const queue = [...items];
  const workerCount = Math.max(1, Math.min(concurrency, items.length));

  const workers = Array.from({ length: workerCount }, async () => {
    while (queue.length > 0) {
      const next = queue.shift();
      if (next === undefined) return;
      await task(next);
    }
  });

  await Promise.all(workers);
};

export const populateDataUrlCacheForUrls = async (
  urls: string[],
  options?: { concurrency?: number }
) => {
  const concurrency = options?.concurrency ?? 4;

  const urlSet = new Set(urls.filter(Boolean));
  const cache = new Map<string, string | null>();

  await runWithConcurrency([...urlSet], concurrency, async (url) => {
    cache.set(url, await tryFetchAsDataUrl(url));
  });

  return cache;
};

export const getFileNameFromUrl = (url: string, fallback: string) => {
  if (!url) return fallback;

  try {
    const parsed = new URL(url);
    const lastSegment = parsed.pathname.split("/").filter(Boolean).pop();
    return decodeURIComponent(lastSegment ?? fallback);
  } catch {
    return fallback;
  }
};

const isDataUrl = (value: string) => value.startsWith("data:");

const getExtensionForMimeType = (mimeType: string) => {
  switch (mimeType.toLowerCase()) {
    case "image/png":
      return ".png";
    case "image/jpeg":
      return ".jpg";
    case "image/jpg":
      return ".jpg";
    case "image/webp":
      return ".webp";
    case "image/gif":
      return ".gif";
    case "image/bmp":
      return ".bmp";
    case "image/svg+xml":
      return ".svg";
    default:
      return "";
  }
};

const ensureFilenameHasExtension = (filename: string, mimeType: string) => {
  const trimmed = filename.trim();
  if (trimmed.length === 0) {
    const ext = getExtensionForMimeType(mimeType);
    return `download${ext}`;
  }

  // If there's already an extension, keep it.
  if (/[.][A-Za-z0-9]{1,8}$/.test(trimmed)) return trimmed;

  const ext = getExtensionForMimeType(mimeType);
  return ext ? `${trimmed}${ext}` : trimmed;
};

const base64ToBlob = (base64: string, mimeType: string) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

const tryParseDataUrl = (dataUrl: string) => {
  const match = dataUrl.match(/^data:([^;,]+)?(;base64)?,(.*)$/);
  if (!match) return null;

  return {
    mimeType: match[1] ?? "application/octet-stream",
    isBase64: match[2] === ";base64",
    payload: match[3] ?? "",
  };
};

const dataUrlToBlob = (dataUrl: string): { blob: Blob; mimeType: string } | null => {
  if (!isDataUrl(dataUrl)) return null;
  const parsed = tryParseDataUrl(dataUrl);
  if (!parsed) return null;

  const blob = parsed.isBase64
    ? base64ToBlob(parsed.payload, parsed.mimeType)
    : new Blob([decodeURIComponent(parsed.payload)], { type: parsed.mimeType });

  return { blob, mimeType: parsed.mimeType };
};

export const downloadDataUrl = (dataUrl: string, filename: string) => {
  const parsed = dataUrlToBlob(dataUrl);
  if (!parsed) return;

  const safeFilename = ensureFilenameHasExtension(filename, parsed.mimeType);
  saveAs(parsed.blob, safeFilename);
};
